import React from 'react';

import { ControllerRenderProps, useFormContext } from 'react-hook-form';

import {
  ERROR_FIELD_CLASS,
  IBlockItemFormProps
} from 'views/Pages/PagesForm/constants';
import {
  ctaItemSchema,
  pageFormSchema
} from 'views/Pages/PagesForm/validation-schema';
import { z } from 'zod';

import Checkbox from 'components/Checkbox/Checkbox';
import Input from 'components/Input/Input';
import { cn } from 'components/lib/utils';
import UploadImage from 'components/UploadImage/UploadImage';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '../../../../../../../components/ui/form';
import { BlockSegmentsField } from '../../Fields/BlockSegmentsField';
import { BlockVisibilityField } from '../../Fields/BlockVisibilityField';

type CtaItemBlockType = z.infer<typeof ctaItemSchema>;
type PageFormType = z.infer<typeof pageFormSchema>;

type CtaItemFieldProps<T extends keyof CtaItemBlockType> = Omit<
  ControllerRenderProps<PageFormType, any>,
  'value'
> & {
  value: CtaItemBlockType[T] | null | undefined;
};

export const CtaItemForm: React.FC<IBlockItemFormProps> = ({
  item,
  blockIndex,
  itemIndex
}) => {
  const { control, getFieldState } =
    useFormContext<z.infer<typeof pageFormSchema>>();

  const basePath = `blocksV2.${blockIndex}.items.${itemIndex}` as const;

  const getFieldError = (fieldName: string) => {
    const fieldState = getFieldState(fieldName as any);
    return fieldState.error;
  };

  return (
    <>
      <FormField
        control={control}
        name={`${basePath}.assetUrl`}
        render={({
          field: { onChange, value }
        }: {
          field: CtaItemFieldProps<'assetUrl'>;
        }) => (
          <FormItem>
            <FormControl>
              <UploadImage
                title="Default Asset"
                description={`Add an asset to your page.`}
                buttonText="Upload"
                onUpload={(url: string) => {
                  onChange(url);
                }}
                className={cn(
                  'w-full',
                  getFieldError(`${basePath}.assetUrl`)?.message ? 'mb-0' : ''
                )}
                selectedImageUrl={value ?? undefined}
                data-testid="PagesForm-Upload-Asset"
              />
            </FormControl>
            <FormMessage className="mt-1 text-p-s text-base-destructive" />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={`${basePath}.assetUrlMobile`}
        render={({
          field: { onChange, value }
        }: {
          field: CtaItemFieldProps<'assetUrlMobile'>;
        }) => (
          <FormItem>
            <FormControl>
              <UploadImage
                title="Mobile Asset"
                description={`Add a mobile asset to your page.`}
                buttonText="Upload"
                onUpload={(url: string) => {
                  onChange(url);
                }}
                className={cn(
                  'w-full',
                  getFieldError(`${basePath}.assetUrlMobile`)?.message
                    ? 'mb-0'
                    : ''
                )}
                selectedImageUrl={value ?? undefined}
                data-testid="PagesForm-Upload-MobileAsset"
              />
            </FormControl>
            <FormMessage className="mt-1 text-p-s text-base-destructive" />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={`${basePath}.redirectUrl`}
        render={({ field }: { field: CtaItemFieldProps<'redirectUrl'> }) => (
          <FormItem>
            <FormLabel>* Redirect URL</FormLabel>
            <FormControl>
              <Input
                placeholder="Enter redirect URL"
                isRequired
                className={cn(
                  getFieldError(`${basePath}.redirectUrl`)?.message
                    ? ERROR_FIELD_CLASS
                    : ''
                )}
                state={
                  getFieldError(`${basePath}.redirectUrl`)?.message
                    ? 'error'
                    : 'default'
                }
                {...field}
                value={field.value ?? ''}
              />
            </FormControl>
            <FormMessage className="mt-1 text-p-s text-base-destructive" />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={`${basePath}.isStoreRedirect` as any}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
                label={'External webstore Redirect'}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={`${basePath}.text` as any}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Button Text</FormLabel>
            <FormControl>
              <Input placeholder="Enter text" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <BlockVisibilityField basePath={basePath} />
      <BlockSegmentsField basePath={basePath} />
    </>
  );
};
